/**
 * The streaming-tail anchor: one trusted mark on the element the cursor
 * belongs to, applied after every other pass has finished moving the tree.
 *
 * Standalone and registered under BOTH typesetting policies, which is the
 * load-bearing part. Riding on incidental last-child selectors instead means
 * suppressing a trailing rule silently re-points the cursor at the wrong
 * node. Folding this into the editorial traversal would strand every
 * plain-policy render — including the fail-open fallback of a message that is
 * still streaming, which is exactly when a cursor matters most.
 *
 * This pass is the only writer of the attribute, so it scrubs every authored
 * spelling before minting its own. Under the plain policy nothing else scrubs
 * it, which makes this the only thing between raw authored HTML and a hijacked
 * cursor.
 */
import {
    classifyEditorialCapabilities,
    type EditorialHastNode,
} from "./editorial-capabilities";
import { isIgnorableNode } from "./editorial-inline-run";
import {
    getEditorialProvenance,
    isEditorialGenerated,
    markEditorial,
    normalizeAttributeName,
} from "./editorial-provenance";

/** hast's normalized spelling of `data-aui-stream-tail`. */
export const STREAM_TAIL_PROPERTY = "dataAuiStreamTail";

// Derived, not re-typed: a hand-written second spelling of the attribute this
// pass mints is a scrub that silently stops matching what it scrubs.
const RESERVED_NAME = normalizeAttributeName(STREAM_TAIL_PROPERTY);

// Nothing that renders no text of its own can hold the cursor. `hr` is the
// specific one that matters: rule normalization may suppress a trailing rule,
// and an anchor sitting on it would vanish with it.
const UNANCHORABLE_TAGS: ReadonlySet<string> = new Set([
    "area",
    "base",
    "br",
    "col",
    "embed",
    "hr",
    "img",
    "input",
    "link",
    "meta",
    "param",
    "source",
    "track",
    "wbr",
]);

// Explicit stack, not recursion. ~10k nested elements is reachable from
// authored markup, and the recursive form threw `RangeError: Maximum call stack
// size exceeded` — which this pass cannot afford, because it runs on BOTH
// policies: the editorial boundary would catch the throw, fall back to plain,
// and plain would run this same pass and throw again. The thread blanks.
//
// `seen` is the other half. A bare stack swap turns a cyclic tree from a
// RangeError into an infinite loop, and a hung tab reports nothing at all.
// Same shape as the scrub in `rehype-editorial-structure.ts`.
const scrubAuthoredAnchors = (node: EditorialHastNode): void => {
    const seen = new Set<unknown>();
    const stack: EditorialHastNode[] = [node];

    while (stack.length > 0) {
        const current = stack.pop() as EditorialHastNode;
        if (seen.has(current)) continue;
        seen.add(current);

        const properties = current.properties;
        if (properties) {
            // Deleted in place rather than rebuilt: the markdown memo comparator
            // tests `properties` by reference, matching the structural pass.
            for (const name of Object.keys(properties)) {
                if (normalizeAttributeName(name) === RESERVED_NAME) {
                    delete properties[name];
                }
            }
        }

        for (const child of current.children ?? []) stack.push(child);
    }
};

/**
 * Content this layer minted, as opposed to a mark it left on source content.
 *
 * Excluded from tail selection for a reason beyond taste: under the editorial
 * policy a block can end with a minted glyph where the plain policy has plain
 * text, and anchoring on the glyph would move the cursor between policies for
 * the same source. Both policies must select the same logical tail.
 *
 * This pass's OWN anchor from a previous run is deliberately not minted
 * content — it is a mark on a source node. Treating it as generated would
 * make the pass skip the node it just chose, walking the cursor back a block
 * on the next run and removing it on the one after.
 */
const isMintedContent = (node: EditorialHastNode): boolean =>
    isEditorialGenerated(node) &&
    getEditorialProvenance(node)?.kind !== "tail-anchor";

/** Renders nothing a cursor could sit beside, so tail selection steps over it. */
const isSkippable = (node: EditorialHastNode): boolean =>
    isIgnorableNode(node) ||
    (node.type === "element" &&
        (UNANCHORABLE_TAGS.has(node.tagName ?? "") || isMintedContent(node)));

/**
 * Whether the cursor may go INSIDE this node, as opposed to sitting on it.
 *
 * Asked of the capability classifier rather than a tag list, because the tag
 * list was answering it by omission: tables are source-model, and the walk
 * descended into one and marked a `<td>` — a node every other pass in this
 * layer treats as untouchable.
 *
 * `pre` is the one deliberate exception, and it is not an oversight in the
 * capability model: the CSS this replaces has a dedicated `pre:last-child code`
 * rule, so the `code` really is where the cursor belongs. Everything else that
 * is not ordinary prose gets the mark on its boundary — which is also where the
 * replaced CSS put it, since `::after` on the block renders after the block.
 */
const mayDescendInto = (node: EditorialHastNode): boolean => {
    const { contract } = classifyEditorialCapabilities(node);
    if (contract === "verbatim") return (node.tagName ?? "") === "pre";

    return contract === "none";
};

/**
 * The element the cursor belongs to: descend only while the last thing in the
 * block is an element that can hold it.
 *
 * The stopping rule is what makes this correct. The CSS this replaces painted
 * `::after` on the last-child BLOCK, which renders after ALL of that block's
 * content — trailing prose included. So a candidate is only the tail if
 * nothing renders after it: a block ending in text anchors the block itself,
 * because moving the mark onto an earlier inline element would put the cursor
 * mid-sentence with the rest of the line still to its right.
 *
 * `pre > code` still resolves to the `code`, since it is the only thing in the
 * `pre` and therefore genuinely terminal.
 *
 * The descent is iterative for the same reason the scrub is: its recursion
 * depth IS the tree depth. `deepest` carries what the recursive form expressed
 * as `?? child` — the last candidate seen, returned when descending finds
 * nothing better.
 */
const deepestTail = (
    node: EditorialHastNode
): EditorialHastNode | undefined => {
    const seen = new Set<unknown>();
    let current: EditorialHastNode = node;
    let deepest: EditorialHastNode | undefined;

    for (;;) {
        if (seen.has(current)) return deepest;
        seen.add(current);

        const children = current.children ?? [];
        let chosen: EditorialHastNode | undefined;

        for (let index = children.length - 1; index >= 0; index--) {
            const child = children[index];
            if (isSkippable(child)) continue;

            // Trailing prose: the node we came from is the tail, not anything
            // inside this one.
            if (child.type !== "element") return deepest;

            chosen = child;
            break;
        }

        if (!chosen) return deepest;
        deepest = chosen;
        if (!mayDescendInto(chosen)) return chosen;
        current = chosen;
    }
};

export const rehypeMarkStreamTail =
    () =>
    (tree: unknown): void => {
        const root = tree as EditorialHastNode;

        // Scrub first, mint second. Every anchor in the tree is authored until
        // this pass says otherwise, including one this pass left on a previous
        // run — which is what keeps "exactly one" true across re-renders.
        scrubAuthoredAnchors(root);

        const target = deepestTail(root);
        if (!target) return;

        const writable = target as { properties?: Record<string, unknown> };
        if (!writable.properties) writable.properties = {};
        writable.properties[STREAM_TAIL_PROPERTY] = "true";

        markEditorial(target, { kind: "tail-anchor" });
    };
